import { Fragment, type ReactNode, type Ref, useMemo, useState } from "react";
import { MY_ROW_TINT } from "@/shared/lib/rowTint";
import { cn } from "@/shared/lib/utils";
import {
  useCopyPreviousBoss,
  useSetBossPlayers,
  useSetBossSkipped,
  useSetPlayerBosses,
  useToggleBossAssignment,
} from "../api/setup.mutations";
import {
  buildAssignmentLookup,
  deriveRowState,
  flattenActivePlayers,
  flattenBossContent,
  type VaultThresholds,
  type VisibleBoss,
} from "../setup.bosses";
import type { PlayerChange } from "../setup.overrides";
import type { SetupPlayer, SetupResponse } from "../setup.types";
import { BossAssignmentCell } from "./BossAssignmentCell";
import { BossHeaderContextMenu } from "./BossHeaderContextMenu";
import { RaidGap, RaidHeaderGap } from "./BossMatrixCells";
import { BossPlayerContextMenu } from "./BossPlayerContextMenu";
import { BossRulesFooter } from "./BossRulesFooter";
import { PlayerLabel } from "./PlayerLabel";
import { VaultCell } from "./VaultCell";

export function BossSetupMatrix({
  raidId,
  data,
  editable,
  onChange,
  onDeleteStandIn,
  onEditStandIn,
  tableRef,
}: {
  raidId: string;
  data: SetupResponse;
  editable: boolean;
  onChange: (player: SetupPlayer, change: PlayerChange) => void;
  onDeleteStandIn: (id: string) => void;
  onEditStandIn: (player: SetupPlayer) => void;
  // Points at the <table> (not the scroll wrapper) so a screenshot captures the
  // full-width matrix regardless of horizontal scroll position.
  tableRef?: Ref<HTMLTableElement>;
}) {
  const content = data.bossContent;
  const toggleAssignment = useToggleBossAssignment(raidId);
  const setPlayerBosses = useSetPlayerBosses(raidId);
  const setBossPlayers = useSetBossPlayers(raidId);
  const copyPreviousBoss = useCopyPreviousBoss(raidId);
  const setBossSkipped = useSetBossSkipped(raidId);
  const [hoveredBossId, setHoveredBossId] = useState<string | null>(null);

  const players = useMemo(
    () => flattenActivePlayers(data.players),
    [data.players],
  );
  const { bosses, groups } = useMemo(
    () => (content ? flattenBossContent(content) : { bosses: [], groups: [] }),
    [content],
  );
  const lookup = useMemo(
    () => (content ? buildAssignmentLookup(content) : new Set<string>()),
    [content],
  );

  if (!content?.isCreated) return null;

  const thresholds: VaultThresholds = [
    content.vaultThreshold1,
    content.vaultThreshold2,
    content.vaultThreshold3,
  ];

  const row = (player: SetupPlayer, index: number): ReactNode => {
    const {
      participantKind,
      roleChanged,
      isLast,
      selections,
      selectedBossCount,
    } = deriveRowState(player, index, players, bosses, lookup);
    const cells = (
      <tr
        tabIndex={editable ? 0 : undefined}
        className="group hover:bg-muted/20 focus-visible:outline-2 focus-visible:outline-ring focus-visible:outline-offset-[-2px]"
      >
        <PlayerNameCell
          player={player}
          roleChanged={roleChanged}
          isLast={isLast}
        />
        <VaultCell
          count={selectedBossCount}
          thresholds={thresholds}
          className={cn(
            "border-l border-border",
            roleChanged && "border-t-2",
            isLast && "border-b",
          )}
        />
        {bosses.map((boss, bossIndex) => {
          const selected = selections[bossIndex];
          return (
            <Fragment key={boss.id}>
              {boss.isGroupStart && bossIndex > 0 && <RaidGap />}
              <BossAssignmentCell
                // Skipped column: render as unselected and inert; the preserved
                // assignment is intentionally not shown.
                selected={boss.skipped ? false : selected}
                editable={editable && !boss.skipped}
                playerName={player.name}
                bossName={boss.name}
                className={cn(
                  !boss.skipped && player.isMe && MY_ROW_TINT,
                  !boss.skipped && player.isMe && "capture-neutral-bg",
                  !boss.skipped && hoveredBossId === boss.id && "bg-muted/30",
                  boss.skipped && "bg-muted/30 opacity-40",
                  "border-l border-border",
                  boss.isGroupEnd && "border-r",
                  roleChanged && "border-t-2",
                  isLast && "border-b",
                )}
                onHover={() => setHoveredBossId(boss.id)}
                onToggle={() =>
                  toggleAssignment.mutate({
                    bossId: boss.id,
                    participantKind,
                    participantId: player.id,
                    selected: !selected,
                  })
                }
              />
            </Fragment>
          );
        })}
      </tr>
    );

    return editable ? (
      <BossPlayerContextMenu
        key={`${participantKind}:${player.id}`}
        player={player}
        onChange={(change) => onChange(player, change)}
        onDeleteStandIn={onDeleteStandIn}
        onEditStandIn={onEditStandIn}
        onSelectAll={() =>
          setPlayerBosses.mutate({
            participantKind,
            participantId: player.id,
            selected: true,
          })
        }
        onSelectNone={() =>
          setPlayerBosses.mutate({
            participantKind,
            participantId: player.id,
            selected: false,
          })
        }
      >
        {cells}
      </BossPlayerContextMenu>
    ) : (
      <Fragment key={`${participantKind}:${player.id}`}>{cells}</Fragment>
    );
  };

  return (
    <div className="overflow-x-auto">
      <table
        ref={tableRef}
        data-col
        className="w-max border-separate border-spacing-0"
        onMouseLeave={() => setHoveredBossId(null)}
      >
        <thead>
          <tr>
            <th
              rowSpan={2}
              scope="col"
              aria-label="Spelare"
              className="sticky left-0 z-20 min-w-40 border-b border-border bg-background px-3 py-2 text-left text-xs font-medium text-muted-foreground"
            />
            <th
              rowSpan={2}
              scope="col"
              className="min-w-16 border-y border-l border-border bg-background px-2 py-2 text-center text-xs font-medium text-muted-foreground"
            >
              Vault
            </th>
            {groups.map((group, groupIndex) => (
              <Fragment key={group.raidId}>
                {groupIndex > 0 && <RaidHeaderGap />}
                <th
                  scope="colgroup"
                  colSpan={group.endIndexExclusive - group.startIndex}
                  className="border border-border bg-muted/40 px-2 py-1.5 text-center text-xs font-semibold"
                >
                  {group.raidName}
                </th>
              </Fragment>
            ))}
          </tr>
          <tr>
            {bosses.map((boss, index) => (
              <BossHeaderCell
                key={boss.id}
                boss={boss}
                editable={editable}
                canCopyPrevious={index > 0}
                highlighted={hoveredBossId === boss.id}
                onHover={() => setHoveredBossId(boss.id)}
                onSelectAll={() =>
                  setBossPlayers.mutate({ bossId: boss.id, selected: true })
                }
                onSelectNone={() =>
                  setBossPlayers.mutate({ bossId: boss.id, selected: false })
                }
                onCopyPrevious={() =>
                  copyPreviousBoss.mutate({ bossId: boss.id })
                }
                onToggleSkip={() =>
                  setBossSkipped.mutate({
                    bossId: boss.id,
                    skipped: !boss.skipped,
                  })
                }
              />
            ))}
          </tr>
        </thead>
        <tbody>{players.map(row)}</tbody>
        <BossRulesFooter data={data} bosses={bosses} />
      </table>
    </div>
  );
}

function PlayerNameCell({
  player,
  roleChanged,
  isLast,
}: {
  player: SetupPlayer;
  roleChanged: boolean;
  isLast: boolean;
}) {
  return (
    <th
      scope="row"
      className={cn(
        "sticky left-0 z-10 min-w-40 max-w-40 border-l border-border px-3 py-2 text-left text-sm font-normal group-hover:bg-muted",
        player.isMe ? MY_ROW_TINT : "bg-background",
        player.isMe && "capture-neutral-bg",
        roleChanged && "border-t-2 border-t-border",
        isLast && "border-b",
      )}
    >
      <div className="flex items-center gap-2">
        <PlayerLabel
          player={player}
          iconLabel="Saknar klass eller roll"
          nameClassName="min-w-0 truncate"
          badgeClassName="ml-auto shrink-0 px-1 py-0.5 text-[10px]"
        />
      </div>
    </th>
  );
}

function BossHeaderCell({
  boss,
  editable,
  canCopyPrevious,
  highlighted,
  onHover,
  onSelectAll,
  onSelectNone,
  onCopyPrevious,
  onToggleSkip,
}: {
  boss: VisibleBoss;
  editable: boolean;
  canCopyPrevious: boolean;
  highlighted: boolean;
  onHover: () => void;
  onSelectAll: () => void;
  onSelectNone: () => void;
  onCopyPrevious: () => void;
  onToggleSkip: () => void;
}) {
  const label = (
    <div
      tabIndex={editable ? 0 : undefined}
      className={cn(
        "relative flex min-h-12 flex-col items-center justify-center gap-0.5 px-2 py-1.5 text-center text-xs font-medium",
        editable &&
          "outline-none hover:bg-muted focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-inset",
      )}
    >
      <span
        className={cn(boss.skipped && "text-muted-foreground line-through")}
      >
        {boss.name}
      </span>
      {boss.skipped && (
        <span className="text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
          Skippad
        </span>
      )}
    </div>
  );

  return (
    <th
      scope="col"
      className={cn(
        "min-w-28 max-w-28 border-b border-l border-border bg-background p-0 align-middle",
        boss.isGroupEnd && "border-r",
        boss.skipped && "bg-muted/20",
        highlighted && !boss.skipped && "bg-muted/30",
      )}
      onMouseEnter={onHover}
    >
      {editable ? (
        <BossHeaderContextMenu
          skipped={boss.skipped}
          canCopyPrevious={canCopyPrevious}
          onSelectAll={onSelectAll}
          onSelectNone={onSelectNone}
          onCopyPrevious={onCopyPrevious}
          onToggleSkip={onToggleSkip}
        >
          {label}
        </BossHeaderContextMenu>
      ) : (
        label
      )}
    </th>
  );
}
